import { useState, useEffect } from 'react'
import { SubmissionCell } from '../../components/SubmissionCell'
import { pb } from '../../pocketbase/Pocketbase'
import { useAuthStore } from '../../data/useAuthStore'
import { useNavigate } from 'react-router-dom'
import { OkDialog } from '../../components/dialogs/OkDialog'

type SubmissionType = 'pending' | 'approved' | 'denied'

interface Submission {
  id: string
  name: string
  slug?: string // Only present for approved submissions
  description: string
  image: string
  created: string
  denied_reason?: string // Only present for denied submissions
}

export const MyFragrancesView = () => {
  const navigate = useNavigate()
  const { user, userProfile } = useAuthStore()
  const [submissionType, setSubmissionType] = useState<SubmissionType>('pending')
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showPendingDialog, setShowPendingDialog] = useState(false)

  useEffect(() => {
    if (user) {
      loadSubmissions()
    }
  }, [submissionType, user])

  const loadSubmissions = async () => {
    if (!user) return
    if (!userProfile) return

    try {
      setIsLoading(true)

      if (submissionType === 'pending') {
        // Fetch pending submissions for this user (not denied)
        const result = await pb.collection('pending_submissions').getList(1, 50, {
          filter: `submitted_by = "${userProfile.id}" && is_denied = false`,
          sort: '-created',
        })
        setSubmissions(result.items as any[])
      } else if (submissionType === 'denied') {
        // Fetch denied submissions for this user
        const result = await pb.collection('pending_submissions').getList(1, 50, {
          filter: `submitted_by = "${userProfile.id}" && is_denied = true`,
          sort: '-created',
        })
        setSubmissions(result.items as any[])
      } else {
        // Fetch approved submissions for this user
        const result = await pb.collection('approved_submissions').getList(1, 50, {
          filter: `submitted_by = "${userProfile.id}"`,
          sort: '-created',
        })
        setSubmissions(result.items as any[])
      }
    } catch (error) {
      console.error('Error loading submissions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    const diffWeeks = Math.floor(diffMs / 604800000)

    if (diffMins < 60) {
      return diffMins <= 1 ? 'Just now' : `${diffMins} minutes ago`
    } else if (diffHours < 24) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`
    } else if (diffDays < 7) {
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`
    } else if (diffWeeks < 4) {
      return diffWeeks === 1 ? '1 week ago' : `${diffWeeks} weeks ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const handleSubmissionClick = (submission: Submission) => {
    if (submissionType === 'pending') {
      // Show dialog for pending submissions
      setShowPendingDialog(true)
    } else {
      // Navigate to details page for approved submissions using slug
      navigate(`/fragrance/${submission.slug}`)
    }
  }

  return (
    <div className={'flex flex-col h-full'}>
      {/* Fixed Header */}
      <div
        className={
          'flex items-center justify-between px-4 md:px-8 pb-6 pt-8 border-b border-searchBorder'
        }
      >
        {/* Dropdown for submission type */}
        <select
          value={submissionType}
          onChange={(e) => setSubmissionType(e.target.value as SubmissionType)}
          className={
            'px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer'
          }
        >
          <option value="pending">Pending Submissions</option>
          <option value="denied">Denied Submissions</option>
          <option value="approved">Approved Submissions</option>
        </select>

        <span className={'text-gray-600'}>
          {isLoading ? '...' : `${submissions.length} submission${submissions.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Scrollable List */}
      <div className={'flex-1 overflow-y-auto pr-2 scrollbar-hide-track'}>
        {isLoading ? (
          <div className={'flex items-center justify-center py-12'}>
            <div className={'text-gray-500'}>Loading...</div>
          </div>
        ) : submissions.length === 0 ? (
          <div className={'flex items-center justify-center py-12'}>
            <div className={'text-gray-500'}>
              No {submissionType} submissions yet
            </div>
          </div>
        ) : (
          submissions.map((submission) => {
            const imageUrl = submission.image
              ? pb.files.getURL(submission, submission.image)
              : 'https://via.placeholder.com/150'

            return (
              <SubmissionCell
                key={submission.id}
                imageUrl={imageUrl}
                title={submission.name}
                description={submission.description || 'No description provided'}
                deniedReason={submission.denied_reason}
                timestamp={formatTimestamp(submission.created)}
                onClick={() => handleSubmissionClick(submission)}
              />
            )
          })
        )}
      </div>

      <OkDialog
        isOpen={showPendingDialog}
        onClose={() => setShowPendingDialog(false)}
        title="Submission Under Review"
        content="Your submission is currently under review. You'll be notified once it has been processed."
      />
    </div>
  )
}
